import { motion } from "framer-motion";
import { CHALLENGES, INTRO_BEATS, INTRO_MEDIA, OBJECTIVE_GROUPS } from "../../constants/verify";
import { CornerMarks, MediaSlot, SectionShell, useFade } from "./ui";

function VerifyStory() {
  const { fade } = useFade();

  return (
    <>
      <SectionShell id="intro" labelledBy="intro-heading" tone="white">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Introduction
            </p>
            <h2
              id="intro-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Building digital trust
              <span className="mt-1 block text-verify-royal">for every financial decision</span>
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy/60 sm:text-lg">
              Organizations make thousands of lending and financing decisions every day. Traditional
              verification often relies on limited information, fragmented systems and manual
              processes.
            </p>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-navy/60 sm:text-lg">
              SimVerify changes this. Developed as part of the SimFinance ecosystem, it is an
              AI-powered Digital Trust Intelligence Platform that verifies both the borrower and the
              asset before critical financial decisions.
            </p>
          </motion.header>

          <div className="relative mt-12">
            <span
              className="pointer-events-none absolute left-[12.5%] right-[12.5%] top-[1.15rem] hidden h-px bg-verify-cyan/50 lg:block"
              aria-hidden="true"
            />
            <ol className="grid items-stretch gap-4 sm:grid-cols-3">
              {INTRO_BEATS.map((item) => (
                <li key={item.n} className="relative flex h-full flex-col border border-navy/10 bg-[#eef3fb] p-5 sm:p-6">
                  <span className="relative z-[1] flex h-9 w-9 items-center justify-center bg-verify-royal font-mono text-[11px] tracking-[0.12em] text-white">
                    {item.n}
                  </span>
                  <h3 className="mt-5 text-lg font-semibold tracking-tight text-navy">{item.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-navy/60">{item.copy}</p>
                </li>
              ))}
            </ol>
          </div>

          <div className="relative mt-10 overflow-hidden border border-navy/10">
            <CornerMarks />
            <div className="relative aspect-[16/10] min-h-[16rem]">
              <MediaSlot
                src={INTRO_MEDIA.src}
                alt={INTRO_MEDIA.alt}
                spec={INTRO_MEDIA.spec}
                className="absolute inset-0 h-full w-full"
                tone="light"
              />
            </div>
          </div>

          <p className="mt-8 max-w-3xl border-l-[3px] border-verify-gold pl-5 text-base leading-relaxed text-navy/70">
            Unlike conventional verification systems, SimVerify combines borrower information, vehicle
            verification, device verification, SIM behavioural intelligence and AI-driven Trust Scores
            into one digital trust profile.
          </p>
        </div>
      </SectionShell>

      <SectionShell id="why-simverify" labelledBy="why-heading" tone="ice">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Why SimVerify matters
            </p>
            <h2
              id="why-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              The challenge facing
              <span className="mt-1 block">modern lending</span>
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy/60 sm:text-lg">
              Financial institutions across Africa continue to experience rising fraud, incomplete
              verification and slow approvals. Credit bureau files alone leave important risks
              undetected.
            </p>
          </motion.header>

          <ol className="mt-12 grid auto-rows-fr gap-px overflow-hidden border border-navy/10 bg-navy/10 sm:grid-cols-2 lg:grid-cols-4">
            {CHALLENGES.map((item, index) => (
              <li key={item} className="flex h-full min-h-[8.5rem] flex-col bg-white p-5">
                <span className="font-mono text-[11px] tracking-[0.16em] text-verify-royal">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <p className="mt-4 text-base font-semibold leading-snug tracking-tight text-navy">{item}</p>
              </li>
            ))}
          </ol>

          <div className="mt-6 border border-navy/10 bg-navy p-6 text-white sm:p-8">
            <p className="font-mono text-[11px] tracking-[0.2em] text-verify-gold">The response</p>
            <p className="mt-3 max-w-3xl text-lg font-semibold leading-snug tracking-tight">
              Organizations need more than credit history — they need confidence in the borrower, the
              asset and the overall transaction.
            </p>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-white/70">
              SimVerify integrates multiple trusted data sources into one intelligent verification
              platform, so institutions can protect customers and the book at the same time.
            </p>
          </div>
        </div>
      </SectionShell>

      <SectionShell id="vision" labelledBy="vision-heading" tone="royal">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
          <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-white/55">Our vision</p>
          <h2
            id="vision-heading"
            className="mt-5 max-w-4xl font-semibold leading-[1.12] tracking-tight [font-size:clamp(1.6rem,3.6vw,2.6rem)]"
          >
            To become Africa&apos;s leading Digital Trust Intelligence Platform, empowering
            organizations with real-time verification, AI-driven insights and trusted decision
            support for every high-value transaction.
          </h2>
        </div>
      </SectionShell>

      <SectionShell id="objectives" labelledBy="objectives-heading" tone="white">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Service objectives
            </p>
            <h2
              id="objectives-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Verify. Decide.
              <span className="mt-1 block text-verify-royal">Protect.</span>
            </h2>
          </motion.header>

          <ol className="mt-12 grid items-stretch gap-4 lg:grid-cols-3">
            {OBJECTIVE_GROUPS.map((group) => (
              <li key={group.id} className="flex h-full flex-col border border-navy/10 bg-[#eef3fb] p-6">
                <span className="font-mono text-[11px] tracking-[0.16em] text-verify-royal">{group.n}</span>
                <h3 className="mt-3 text-2xl font-semibold tracking-tight text-navy">{group.title}</h3>
                <p className="mt-2 text-sm text-navy/55">{group.copy}</p>
                <ul className="mt-6 flex flex-1 flex-col gap-2">
                  {group.items.map((item) => (
                    <li key={item} className="flex items-start gap-3 bg-white px-3 py-3 text-sm font-semibold leading-snug text-navy">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-verify-green" />
                      {item}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        </div>
      </SectionShell>
    </>
  );
}

export default VerifyStory;
